import { CustomToast } from "@worship-devo/ui";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

/*
Rules
1. Put values in ToastMessages and ToastTestIds when the toast is shared or stable:
      - Used in 2+ places, or expected to be reused
      - User-facing copy should remain consistent across flows
      - QA/E2E tests will assert it via data-testid
      - Analytics or product language should stay canonical

2. Keep message/testId local (hardcoded at the call site) only when the toast is truly one-off:
      - Single route or component context
      - Highly contextual or dynamic message (e.g., server error details)
      - Not reused and not expected to become reusable

3. If one is centralized, centralize the pair:
      - Avoid adding ToastMessages.X without ToastTestIds.X (or vice versa), unless there is a deliberate reason documented in a comment.
*/

export const ToastMessages = {
  EPISODE_SHARED: "Episode shared",
  SERIES_SHARED: "Series shared",
  APP_SHARED: "Shared",
  LINK_COPIED: "Link copied",
  CHANGE_SAVED: "Changes saved",
  NOTIFICATION_PREFERENCE_SAVED: "Notification preference saved",
  PLEASE_TRY_AGAIN: "Please try again",
  NOTIFICATION_PREFERENCE_UPDATE_ERROR: "Failed to save preferences",
  PHONE_STEP_ERROR: "Phone verification failed",
  VERIFY_STEP_ERROR: "Unable to verify code",
  PROFILE_UPDATE_ERROR: "Failed to update profile",
  GENERIC_ERROR: "Something went wrong",
  TIMEOUT_ERROR: "Taking longer than expected",
  CONNECTION_ERROR: "No internet connection",
  QR_CODE_ERROR: "Failed to load QR code image",
  SERIES_ERROR: "Series not found",
  SIGNED_IN: "Signed In",
  WELCOME: "Welcome",
  CODE_RESENT: "Code sent",
} as const;

export const ToastTestIds = {
  SERIES_ERROR: "series-not-found-toast",
  EPISODE_SHARED: "episode-shared-toast",
  SERIES_SHARED: "series-shared-toast",
  APP_SHARED: "app-shared-toast",
  LINK_COPIED: "share-link-copied-toast",
  CHANGE_SAVED: "change-saved-toast",
  PROFILE_UPDATE_ERROR: "profile-update-error-toast",
  NOTIFICATION_PREFERENCE_SAVED: "notification-preference-saved-toast",
  NOTIFICATION_PREFERENCE_UPDATE_ERROR: "notification-preference-update-error-toast",
  PHONE_STEP_ERROR: "auth-phone-step-error-toast",
  VERIFY_STEP_ERROR: "auth-verify-step-error-toast",
  NAME_STEP_ERROR: "auth-name-step-error-toast",
  GENERIC_ERROR: "generic-error-toast",
  TIMEOUT_ERROR: "timeout-error-toast",
  CONNECTION_ERROR: "connection-error-toast",
  QR_CODE_ERROR: "qr-code-error-toast",
  STREAK_TOAST: "streak-celebration-toast",
  SIGNED_IN: "signed-in-toast",
  WELCOME: "welcome-toast",
  CODE_RESENT: "code-resent-toast",
  SHARE_LANDING: "share-landing-toast",
  SUPPORT_CHAT_UNAVAILABLE: "support-chat-unavailable-toast",
} as const;

export interface ShareToastProps {
  message: string;
  testId: string;
  duration?: number;
  icon?: React.ElementType;
  type: "success" | "error" | "warning" | "info" | "neutral";
  action?: {
    label?: string;
    icon?: React.ElementType;
    ariaLabel?: string;
    onClick?: () => void;
    closeOnClick?: boolean;
  };
  description?: string;
  filled?: boolean;
  redirect?: () => void;
}

export function showCustomToast({
  message,
  testId,
  duration = 3000,
  icon,
  type,
  action,
  description,
  filled = false,
  redirect,
}: ShareToastProps) {
  const wrappedAction = action
    ? {
        ...action,
        onClick: () => {
          action.onClick?.();

          if (action.closeOnClick !== false) {
            toast.dismiss();
          }
        },
      }
    : undefined;

  toast.custom(
    () => (
      <CustomToast
        icon={icon}
        message={message}
        testId={testId}
        duration={duration}
        type={type}
        action={wrappedAction}
        description={description}
        filled={filled}
        redirect={redirect}
      />
    ),
    {
      duration,
    },
  );
}

export function useAuthToastMessages() {
  const { t } = useTranslation("app");
  return {
    pleaseRetry: t("auth.toasts.pleaseRetry"),
    phoneStepError: t("auth.toasts.phoneStepError"),
    verifyStepError: t("auth.toasts.verifyStepError"),
    codeSent: t("auth.toasts.codeSent"),
    signedIn: t("auth.toasts.signedIn"),
    contactSupport: t("auth.toasts.contactSupport"),
  };
}
